import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";

/* One header and one footer for every public page, so the marketing site
   cannot drift apart again the way the old dark Pricing page did. */

export function Wordmark() {
  return (
    <span className="flex items-center gap-2">
      <img src="/brand/bicarra-icon.svg" alt="" className="w-[26px] h-[26px] rounded-[7px]" />
      <span className="text-[19px] font-bold tracking-[-0.03em] text-b2-ink lowercase">bicarra</span>
    </span>
  );
}

export function Primary({ to, children, testid, className = "" }) {
  return (
    <Link
      to={to} data-testid={testid}
      className={`inline-flex items-center justify-center h-11 px-6 rounded-[10px] bg-b2-indigo
                  hover:bg-b2-indigohover text-white text-[15px] font-semibold transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}

export function Secondary({ to, children, testid, className = "" }) {
  return (
    <Link
      to={to} data-testid={testid}
      className={`inline-flex items-center justify-center h-11 px-6 rounded-[10px] bg-white
                  border border-b2-linestrong text-b2-ink text-[15px] font-semibold
                  hover:border-b2-indigo transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}

/* Active nav item gets the indigo underline from the design. */
const navLink = ({ isActive }) =>
  `relative text-[14px] font-medium transition-colors pb-1 ${
    isActive
      ? "text-b2-ink font-semibold after:absolute after:left-0 after:right-0 after:-bottom-px " +
        "after:h-[2px] after:rounded-full after:bg-b2-indigo"
      : "text-b2-inksoft hover:text-b2-ink"
  }`;

export function MarketingHeader() {
  /* Mobile nav lives in the same header so the links never drift from
     the desktop ones. */
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  /* Escape has to be caught on the document. Once the panel is open the user
     has usually tapped away from the header, so a handler bound to the header
     itself would never fire. */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    /* Opaque on purpose: a translucent bar over a full-bleed pale panel
       lets the panel bleed through the nav links. */
    <header className="sticky top-0 z-40 bg-b2-canvas">
      <div className="max-w-[1120px] mx-auto px-6 h-[72px] flex items-center justify-between gap-6">
        <Link to="/"><Wordmark /></Link>
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/#how" className="text-[14px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
            How it works
          </Link>
          <NavLink to="/pricing" className={navLink}>Pricing</NavLink>
          <NavLink to="/blog" className={navLink}>Blog</NavLink>
          <NavLink to="/download" className={navLink}>Get the app</NavLink>
        </nav>
        <div className="flex items-center gap-4">
          {/* Hidden on phones: at 375px the logo, both auth links and the
              hamburger do not fit, and everything wraps. Sign in moves into
              the panel instead; Get started stays because it is the CTA. */}
          <Link to="/login" data-testid="login-nav-btn"
                className="hidden md:inline text-[14px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors whitespace-nowrap">
            Sign in
          </Link>
          <Primary to="/register" testid="get-started-btn" className="whitespace-nowrap">Get started</Primary>
          {/* Hamburger only exists below md, where the desktop nav is hidden. */}
          <button
            type="button" data-testid="mobile-nav-toggle"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-[10px]
                       border border-b2-linestrong text-b2-ink hover:border-b2-indigo transition-colors"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        /* Same order as the desktop nav, then the auth links. */
        <nav
          className="md:hidden border-t border-b2-line bg-b2-surface"
          aria-label="Mobile navigation"
        >
          <div className="max-w-[1120px] mx-auto px-6 py-4 flex flex-col items-start gap-4">
            <Link to="/#how" onClick={closeMobile}
                  className="text-[15px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
              How it works
            </Link>
            <Link to="/pricing" onClick={closeMobile}
                  className="text-[15px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
              Pricing
            </Link>
            <Link to="/blog" onClick={closeMobile}
                  className="text-[15px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
              Blog
            </Link>
            <Link to="/download" onClick={closeMobile}
                  className="text-[15px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
              Get the app
            </Link>
            <Link to="/login" onClick={closeMobile}
                  className="text-[15px] font-medium text-b2-inksoft hover:text-b2-ink transition-colors">
              Sign in
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="border-t border-b2-line">
      <div className="max-w-[1120px] mx-auto px-6 py-10 flex items-start justify-between gap-8 flex-wrap">
        <div>
          <Wordmark />
          <p className="mt-2 text-[12.5px] text-b2-faint">AI with Adab &amp; Akhlak</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-3">
          <nav className="flex items-center gap-7">
            <Link to="/download" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Get the app</Link>
            <Link to="/blog" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Blog</Link>
            <Link to="/pricing" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Pricing</Link>
            <Link to="/contact-us" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Contact</Link>
            <Link to="/privacy" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Privacy</Link>
            <Link to="/terms" className="text-[13.5px] text-b2-inksoft hover:text-b2-ink">Terms</Link>
          </nav>
          <p className="text-[12.5px] text-b2-faint">
            © {new Date().getFullYear()} Bicarra by Veltrix
          </p>
        </div>
      </div>
    </footer>
  );
}
